import { requiredFieldsOf } from "@shared/validation/firestoreDocumentFields.js";
import { DocumentReference, Timestamp } from "firebase/firestore";

// Interface for individual trial locations
export interface TrialLocation {
  name: string;
  startDate: Timestamp;
  endDate: Timestamp | null;
  plotMapUrl?: string; // URL to uploaded plot map file
}

// Type for reading from the database
export interface Trial {
  // Core properties
  id?: string;
  name: string;
  description: string | null;
  orgId: string; // Organization ID - required for security rules

  // Location and crop details
  locationName: string | null; // For backward compatibility with single location trials
  locations: TrialLocation[] | null; // For new multi-location trials
  hasMultipleLocations: boolean; // Flag to indicate if this trial uses multiple locations
  crops: string[] | null;

  // Timing properties (for backward compatibility with single location)
  startDate: Timestamp;
  endDate: Timestamp | null;

  // Collaborators
  collaboratorIds?: string[]; // Array of user IDs who have access to this trial

  // Free-form, like Experiment.metadata and Task.metadata. Ground-truth curation stores its
  // registry key under metadata.groundTruth (see scripts/lib/groundTruth/provenance.ts).
  // Not read by the UI. Optional and additive: iOS FSTrial decodes unknown keys harmlessly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: DocumentReference;
  updatedBy: DocumentReference;
}

/**
 * Every field a stored trial must carry, derived from `Trial` itself.
 *
 * Pass this to `assertCompleteDocument` in any code that writes a trial — app
 * services and admin scripts alike. A trial missing `startDate` does not fail on
 * write (Firestore drops undefined fields), it takes down the trials list for
 * the whole organization on read.
 *
 * Adding a required property to `Trial` will fail the build here until it is
 * listed, which is the point: the interface stays the single source of truth.
 */
export const TRIAL_REQUIRED_FIELDS = requiredFieldsOf<Trial>()([
  "name",
  "description",
  "orgId",
  "locationName",
  "locations",
  "hasMultipleLocations",
  "crops",
  "startDate",
  "endDate",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
] as const);
